/**
 * spacePricingSlice — per-space pricing grids (rows of `space × session`).
 *
 * Lazy-loaded per space when a pricing card is first expanded (no SSR hydration
 * — there can be many spaces). The table dispatches add/update/delete with the
 * server-returned row after a successful API call.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { SpaceSessionPricingRow } from "@/components/settings/venues/pricing/types";

export interface SpacePricingState {
  rowsBySpace: Record<string, SpaceSessionPricingRow[]>;
  /** Spaces whose grid has been fetched at least once. */
  loaded: Record<string, boolean>;
  /** Spaces with an in-flight fetch. */
  loading: Record<string, boolean>;
}

const initialState: SpacePricingState = {
  rowsBySpace: {},
  loaded: {},
  loading: {},
};

const bySortOrder = (a: SpaceSessionPricingRow, b: SpaceSessionPricingRow) =>
  a.sortOrder - b.sortOrder;

const spacePricingSlice = createSlice({
  name: "spacePricing",
  initialState,
  reducers: {
    setSpacePricingLoading(
      state,
      action: PayloadAction<{ spaceId: string; loading: boolean }>,
    ) {
      state.loading[action.payload.spaceId] = action.payload.loading;
    },
    /** Replace one space's grid — used to hydrate on first expand. */
    setSpacePricing(
      state,
      action: PayloadAction<{
        spaceId: string;
        rows: SpaceSessionPricingRow[];
      }>,
    ) {
      state.rowsBySpace[action.payload.spaceId] = [...action.payload.rows].sort(
        bySortOrder,
      );
      state.loaded[action.payload.spaceId] = true;
      state.loading[action.payload.spaceId] = false;
    },
    upsertSpacePricingRow(
      state,
      action: PayloadAction<{ spaceId: string; row: SpaceSessionPricingRow }>,
    ) {
      const { spaceId, row } = action.payload;
      const rows = state.rowsBySpace[spaceId] ?? [];
      const idx = rows.findIndex((r) => r.rowId === row.rowId);
      if (idx === -1) rows.push(row);
      else rows[idx] = row;
      state.rowsBySpace[spaceId] = [...rows].sort(bySortOrder);
    },
    removeSpacePricingRow(
      state,
      action: PayloadAction<{ spaceId: string; rowId: string }>,
    ) {
      const { spaceId, rowId } = action.payload;
      state.rowsBySpace[spaceId] = (state.rowsBySpace[spaceId] ?? []).filter(
        (r) => r.rowId !== rowId,
      );
    },
  },
});

export const {
  setSpacePricingLoading,
  setSpacePricing,
  upsertSpacePricingRow,
  removeSpacePricingRow,
} = spacePricingSlice.actions;

export default spacePricingSlice.reducer;
